import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import ContentContainer from '../../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../commonComponents/pageLayout/header/TopicBanner';
import Title from '../../../commonComponents/Title';
import {
  compose,
  GAME_TYPE,
  displayUserName,
} from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import Select from '../../../commonComponents/forms/inputs/Select';
import TeamProp from '../../../commonComponents/propTypes/teamProp';
import Loader from '../../../services/loader';
import Anchor from '../../../commonComponents/Anchor';
import Notifications from '../../../services/notifications';
import Performance from '../../challenges/details/Performance';
import Leaderboard from '../../challenges/details/Leaderboard';
import GameTypeProp from '../../challenges/propTypes/gameTypeProp';
import ReportTabBtns from '../tab/ReportTabBtns';
import { REPORT_TAB_TYPE } from '../propTypes/reportTabProp';

function ReportPerform({ t, _csrf, teams, gameTypes }) {
  const isScoring = gameTypes.find(g => g.type === 'productivity');
  // Responsible to change tabs
  const [filterActive, setFilterActive] = useState('perform');
  const activeScorm =
    filterActive === 'scorm'
      ? 'commissions-filter-active'
      : 'commissions-filter-inactive';
  const activePerform =
    filterActive === 'perform'
      ? 'commissions-filter-active'
      : 'commissions-filter-inactive';
  const handleTab = tab => {
    setFilterActive(tab);
  };

  const loader = useRef(new Loader('#scormReportsLayout'));
  const [isLoading, setLoading] = useState(false);
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  // Show Performance Report
  const [showPerformance, setShowPerformance] = useState(false);
  const showReport = () => {
    setShowPerformance(true);
  };

  const defaultTeam = teams.length > 0 ? teams[0]._id : null;
  const [filters, setFilters] = useState({
    team: defaultTeam,
    user: null,
    challenge: null,
  });
  const handleFilterChange = field => value => {
    if (field === 'team') {
      setFilters({ team: value, user: null, challenge: null });
      setShowPerformance(false);
    } else if (field === 'user') {
      setFilters(prevState => ({
        team: prevState.team,
        user: value,
        challenge: null,
      }));
      setShowPerformance(false);
    } else {
      setShowPerformance(false);
      setFilters(prevState => ({ ...prevState, [field]: value }));
    }
  };
  // Challenges State
  const [challengesList, setChallengesList] = useState([]);
  /** API REQUESTS */
  const getChallenges = () => {
    setLoading(true);
    const params = {};
    let url = '/api/game/list/managers';
    if (isScoring) {
      url += `?gameType=${isScoring._id}`;
    }
    if (filters.user) {
      params.user = filters.user;
    }
    axios
      .get(url, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setChallengesList(response.data);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_failed_getting_data');
        setLoading(false);
      });
  };
  useEffect(() => {
    getChallenges();
  }, [filters.user]);

  // Selects the correct tab
  let challengeType;
  if (filters.challenge) {
    challengeType = filters.challenge.gameType
      ? filters.challenge.gameType.type
      : GAME_TYPE.PRODUCTIVITY;
  }

  const usersTeamMembers =
    teams
      ?.find(te => te._id === filters.team)
      ?.members?.map(te => ({
        id: te._id,
        text: displayUserName(te),
      })) || [];
  return (
    <Title title={t('myReports')}>
      <ContentContainer>
        <SubHeader title={t('myReports')} />
        <TopicBanner />
        <div className="m-content">
          <div className="full-page-container-with-subheader">
            <div id="scormReportsLayout">
              <div className="row">
                <div className="col-lg-12">
                  <div className="m-portlet m-portlet--mobile">
                    <div className="report-filters-content">
                      <ReportTabBtns tabActive={REPORT_TAB_TYPE.PERFORM} />
                      <div className="row">
                        <div className="row col-lg-12 report-filters-inputs">
                          <div className="col-lg-3">
                            <Select
                              id="filerTeam"
                              name={t('reports.scorm.filter.team')}
                              placeholder={t(
                                'reports.scorm.filter.team.placeholder',
                              )}
                              type="select2"
                              onChange={handleFilterChange('team')}
                              value={filters.team}
                              className="form-control m-input"
                              multiple={false}
                              items={teams.map(te => ({
                                id: te._id,
                                text: te.name,
                              }))}
                            />
                          </div>
                          <div className="col-lg-3">
                            <Select
                              id="filterUser"
                              name={t('reports.scorm.filter.member')}
                              placeholder={t(
                                'reports.scorm.filter.member.placeholder',
                              )}
                              type="select2"
                              onChange={handleFilterChange('user')}
                              value={filters.user}
                              disabled={!filters.team}
                              className="form-control m-input"
                              multiple={false}
                              items={usersTeamMembers}
                            />
                          </div>
                          <div className="col-lg-3">
                            <Select
                              id="filterChallenge"
                              name={t('team.report.perform.select')}
                              placeholder={t('team.report.perform.select')}
                              type="select2"
                              onChange={handleFilterChange('challenge')}
                              value={filters.challenge}
                              disabled={filters.user === null}
                              className="form-control m-input"
                              multiple={false}
                              items={challengesList.map(c => ({
                                id: c._id,
                                text: c.name,
                              }))}
                            />
                          </div>
                          <div className="col-lg-3">
                            <button
                              type="button"
                              onClick={showReport}
                              disabled={filters.challenge === null}
                              className="btn btn-dynamic"
                            >
                              {t('reports.scorm.see_results')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  {filters.challenge && showPerformance && (
                    <div className="m-portlet m-portlet--mobile">
                      <Performance
                        user={filters.user}
                        challenge={challengesList.find(
                          c => c._id === filters.challenge,
                        )}
                        challengeType={challengeType}
                        _csrf={_csrf}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                {filters.challenge && showPerformance && (
                  <div className="m-portlet m-portlet--mobile">
                    <Leaderboard
                      showNewScore={false}
                      challenge={challengesList.find(
                        c => c._id === filters.challenge,
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

ReportPerform.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape(TeamProp.propType)),
  gameTypes: PropTypes.arrayOf(PropTypes.shape(GameTypeProp.propTypes))
    .isRequired,
};

ReportPerform.defaultProps = {
  teams: [],
};

export default compose(translate)(ReportPerform);
